import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BarChart3, Users, TrendingUp, TrendingDown, Clock, ArrowUpDown, ChevronDown, ChevronUp, Activity, Shield, AlertTriangle, GitCompareArrows, X } from 'lucide-react';
import { ListenerSamplingPulse } from './ListenerSamplingPulse';
import { Button } from '../ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { useLanguage } from '../../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Fallback (used only if useLanguage is unavailable). The real labels
// are built inside the component using `t()`.
const MODE_LABELS = {
  audience: {
    title: 'Στατιστικά Ακροατών',
    peak: 'Peak',
    avg: 'Avg',
    min: 'Min',
    tlh: 'TLH',
    unit: 'ακροατές',
    icon: Users,
  },
  activity: {
    title: 'Stream Analytics',
    peak: 'Peak Activity',
    avg: 'Avg Activity',
    min: 'Min Activity',
    tlh: 'Engagement Load',
    unit: 'activity',
    icon: Activity,
  }
};

const MUSIC_TYPE_OPTIONS = [
  { value: 'greek', label: 'Ελληνική' },
  { value: 'international', label: 'Διεθνής / Ξένη' },
  { value: 'rock', label: 'Ροκ' },
  { value: 'entechno', label: 'Έντεχνη' },
  { value: 'mixed', label: 'Μικτή' },
  { value: 'classic_hits', label: 'Classic Hits' },
  { value: 'sophisticated', label: 'Sophisticated' },
];

const GROUP_OPTIONS = [
  { value: 'group_antenna', label: 'Group Antenna' },
  { value: 'group_attikes', label: 'Group Αττικές Εκδόσεις' },
  { value: 'group_audiomax', label: 'Group Audiomax' },
  { value: 'group_frontstage', label: 'Group Frontstage' },
  { value: 'group_kostaki', label: 'Group Κωστάκη' },
  { value: 'group_politis', label: 'Group Politis' },
  { value: 'group_skai', label: 'Group Skai' },
  { value: 'group_independent', label: 'Group Ανεξάρτητος' },
];

// --- Extracted stable sub-components (avoid no-unstable-nested-components) ---
const SortHeader = ({ label, field, sortKey, sortDir, onSort }) => (
  <th
    className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer select-none hover:text-blue-400 transition-colors"
    onClick={() => onSort(field)}
    data-testid={`sort-${field}`}
  >
    <span className="inline-flex items-center gap-1">
      {label}
      {sortKey === field && (
        <span className="text-blue-400">{sortDir === 'asc' ? '↑' : '↓'}</span>
      )}
    </span>
  </th>
);

const ChartTooltipContent = ({ active, payload, label, viewMode }) => {
  if (!active || !payload?.length) return null;
  const labelMap = viewMode === 'activity'
    ? { Peak: 'Peak Activity', Avg: 'Avg Activity', Min: 'Min Activity', TLH: 'Engagement Load' }
    : {};
  return (
    <div className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-xs shadow-xl">
      <div className="font-semibold text-gray-200 mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-gray-400">{labelMap[p.name] || p.name}:</span>
          <span className="text-gray-100 font-medium">
            {p.name?.includes('TLH') ? p.value?.toFixed(2) : p.value?.toLocaleString('el-GR')}
          </span>
        </div>
      ))}
    </div>
  );
};

const SummaryCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-gray-800/60 border border-gray-700/50 rounded-lg p-3 flex items-center gap-3" data-testid={`summary-${String(label).toLowerCase()}`}>
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
      <Icon className="w-4.5 h-4.5 text-white" />
    </div>
    <div>
      <div className="text-xs text-gray-400 uppercase tracking-wide">{label}</div>
      <div className="text-lg font-bold text-gray-100">{value}</div>
    </div>
  </div>
);

const CompareSummaryCard = ({ label, valueA, valueB, icon: Icon, color }) => (
  <div className="bg-gray-800/60 border border-gray-700/50 rounded-lg p-3" data-testid={`compare-summary-${String(label).toLowerCase()}`}>
    <div className="flex items-center gap-2 mb-2">
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-3.5 h-3.5 text-white" />
      </div>
      <span className="text-xs text-gray-400 uppercase tracking-wide">{label}</span>
    </div>
    <div className="grid grid-cols-2 gap-2">
      <div className="text-center">
        <div className="text-lg font-bold text-cyan-300">{valueA}</div>
      </div>
      <div className="text-center">
        <div className="text-lg font-bold text-amber-300">{valueB}</div>
      </div>
    </div>
  </div>
);

const ListenerReport = ({ token }) => {
  const { t } = useLanguage();
  // Build language-aware label mappings each render.
  const MUSIC_TYPE_OPTIONS_I18N = [
    { value: 'greek', label: t('i18nB3_genre_greek') },
    { value: 'international', label: t('i18nB3_genre_intl') },
    { value: 'rock', label: t('i18nB3_genre_rock') },
    { value: 'entechno', label: t('i18nB3_genre_artistic') },
    { value: 'mixed', label: t('i18nB3_genre_mixed') },
    { value: 'classic_hits', label: 'Classic Hits' },
    { value: 'sophisticated', label: 'Sophisticated' },
  ];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [viewMode, setViewMode] = useState('audience');
  const [selectedStation, setSelectedStation] = useState('global');
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    return d.toISOString().split('T')[0];
  });
  const [stations, setStations] = useState([]);
  const [sortKey, setSortKey] = useState('hour');
  const [sortDir, setSortDir] = useState('asc');

  // Compare mode state
  const [compareMode, setCompareMode] = useState(false);
  const [compareType, setCompareType] = useState('station'); // station, group, category
  const [compareA, setCompareA] = useState('');
  const [compareB, setCompareB] = useState('');
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);

  // v2.10.0 i18n Batch #3 — language-aware MODE_LABELS built per render
  // so titles/units follow the current LanguageContext language.
  const MODE_LABELS_I18N = {
    audience: {
      title: t('i18nB3_listener_stats'),
      peak: 'Peak', avg: 'Avg', min: 'Min', tlh: 'TLH',
      unit: t('i18nB3_listeners'), icon: Users,
    },
    activity: {
      title: 'Stream Analytics',
      peak: 'Peak Activity', avg: 'Avg Activity', min: 'Min Activity', tlh: 'Engagement Load',
      unit: 'activity', icon: Activity,
    },
  };
  const labels = MODE_LABELS_I18N[viewMode] || MODE_LABELS[viewMode];

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  }), [token]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date: selectedDate });
      if (selectedStation && selectedStation !== 'global') {
        params.set('station_id', selectedStation);
      }
      const endpoint = viewMode === 'activity'
        ? `${API}/api/stream-monitor/activity-report`
        : `${API}/api/stream-monitor/listener-report`;
      const res = await fetch(`${endpoint}?${params}`, { headers });
      if (res.ok) {
        const json = await res.json();
        setData(json);
        if (json.stations_available && json.stations_available.length > 0) {
          setStations(json.stations_available);
        }
      }
    } catch (e) {
      console.error('Error fetching report:', e);
    } finally {
      setLoading(false);
    }
  }, [selectedStation, selectedDate, token, viewMode, headers]);

  useEffect(() => {
    if (!compareMode) fetchReport();
  }, [fetchReport, compareMode]);

  const fetchCompareData = async () => {
    if (!compareA || !compareB || compareA === compareB) return;
    setCompareLoading(true);
    try {
      const params = new URLSearchParams({
        date: selectedDate,
        compare_mode: compareType,
        entity_a: compareA,
        entity_b: compareB
      });
      const res = await fetch(`${API}/api/stream-monitor/listener-report/compare?${params}`, { headers });
      if (res.ok) {
        const json = await res.json();
        setCompareData(json);
      }
    } catch (e) {
      console.error('Error fetching compare data:', e);
    } finally {
      setCompareLoading(false);
    }
  };

  const sortedHourly = data?.hourly
    ? [...data.hourly].sort((a, b) => {
        const aVal = a[sortKey] ?? 0;
        const bVal = b[sortKey] ?? 0;
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      })
    : [];

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'hour' ? 'asc' : 'desc');
    }
  };

  const formatHour = (h) => `${String(h).padStart(2, '0')}:00`;
  const formatNum = (n) => n != null ? n.toLocaleString('el-GR') : '—';
  const formatTLH = (n) => n != null ? n.toLocaleString('el-GR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

  const summary = data?.daily_summary;

  // Regular chart data
  const chartData = useMemo(() => {
    const hourly = data?.hourly;
    if (!hourly) return [];
    return [...hourly]
      .sort((a, b) => a.hour - b.hour)
      .map(row => ({
        name: `${String(row.hour).padStart(2, '0')}:00`,
        Peak: row.samples > 0 ? row.peak : 0,
        Avg: row.samples > 0 ? row.avg : 0,
        Min: row.samples > 0 ? row.min : 0,
        TLH: row.samples > 0 ? row.tlh : 0,
        hasData: row.samples > 0,
      }));
  }, [data]);

  const hasChartData = chartData.some(d => d.hasData);

  // Compare chart data
  const compareChartData = useMemo(() => {
    if (!compareData) return [];
    const hourlyA = compareData.entity_a?.hourly || [];
    const hourlyB = compareData.entity_b?.hourly || [];
    return Array.from({ length: 24 }, (_, h) => {
      const a = hourlyA.find(r => r.hour === h) || {};
      const b = hourlyB.find(r => r.hour === h) || {};
      return {
        name: `${String(h).padStart(2, '0')}:00`,
        [`${compareData.entity_a?.label || 'A'} Peak`]: a.peak || 0,
        [`${compareData.entity_a?.label || 'A'} Avg`]: a.avg || 0,
        [`${compareData.entity_b?.label || 'B'} Peak`]: b.peak || 0,
        [`${compareData.entity_b?.label || 'B'} Avg`]: b.avg || 0,
        hasData: (a.samples || 0) > 0 || (b.samples || 0) > 0,
      };
    });
  }, [compareData]);

  const legendFormatter = (value) => {
    if (viewMode !== 'activity') return value;
    const map = { Peak: 'Peak Activity', Avg: 'Avg Activity', Min: 'Min Activity', TLH: 'Engagement Load' };
    return map[value] || value;
  };

  const renderTooltipContent = useCallback(
    (props) => <ChartTooltipContent {...props} viewMode={viewMode} />,
    [viewMode]
  );

  const toggleCompare = () => {
    setCompareMode(!compareMode);
    setCompareData(null);
    setCompareA('');
    setCompareB('');
  };

  // Get compare options based on type
  const getCompareOptions = () => {
    if (compareType === 'station') return stations.map(s => ({ value: s.id, label: s.name }));
    if (compareType === 'group') return GROUP_OPTIONS;
    return MUSIC_TYPE_OPTIONS_I18N;
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden" data-testid="listener-report">
      {/* Collapsible Summary Bar */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        data-testid="listener-report-toggle"
      >
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1.5 font-medium text-gray-700 dark:text-gray-300">
            {viewMode === 'activity'
              ? <Activity className="w-4 h-4 text-emerald-500" />
              : <BarChart3 className="w-4 h-4 text-blue-500" />
            }
            <span>{labels.title}</span>
          </div>

          {summary && !compareMode && (
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1 text-rose-500 dark:text-rose-400">
                <TrendingUp className="w-3.5 h-3.5" />
                <span className="font-medium">{formatNum(summary.peak)}</span>
                <span className="text-xs text-gray-400">{labels.peak.toLowerCase()}</span>
              </span>
              <span className="flex items-center gap-1 text-blue-500 dark:text-blue-400">
                <Users className="w-3.5 h-3.5" />
                <span className="font-medium">{formatNum(summary.avg)}</span>
                <span className="text-xs text-gray-400">{viewMode === 'activity' ? 'avg' : t('i18nB3_avg_short')}</span>
              </span>
              <span className="hidden sm:flex items-center gap-1 text-emerald-500 dark:text-emerald-400">
                <Clock className="w-3.5 h-3.5" />
                <span className="font-medium">{formatTLH(summary.tlh)}</span>
                <span className="text-xs text-gray-400">{viewMode === 'activity' ? 'load' : 'TLH'}</span>
              </span>
            </div>
          )}
          {compareMode && (
            <span className="text-xs text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded border border-cyan-500/20">
              Σύγκριση
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 text-gray-400">
          <span className="text-xs hidden md:inline text-gray-500">{compareMode ? 'Compare Mode' : (data?.station_name || '')}</span>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-4 bg-gray-900/80">
          {/* Mode Toggle */}
          <div className="flex items-center gap-3" data-testid="mode-toggle-container">
            <div className="flex items-center gap-0.5 bg-gray-800/80 rounded-lg p-0.5 border border-gray-700/50" data-testid="mode-toggle">
              <button
                onClick={() => setViewMode('audience')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  viewMode === 'audience'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-400 hover:text-gray-200'
                }`}
                data-testid="mode-toggle-audience"
              >
                Live Audience
              </button>
              <button
                onClick={() => setViewMode('activity')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  viewMode === 'activity'
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'text-gray-400 hover:text-gray-200'
                }`}
                data-testid="mode-toggle-activity"
              >
                Stream Analytics
              </button>
            </div>
            {viewMode === 'activity' && (
              <span className="text-xs text-emerald-400/70 bg-emerald-500/10 px-2 py-1 rounded border border-emerald-500/20" data-testid="activity-mode-badge">
                activity-based
              </span>
            )}
            {viewMode === 'activity' && data?.calibration && (
              <span
                className={`text-xs px-2 py-1 rounded border ${
                  data.calibration.correlation_score >= 0.85
                    ? 'text-green-400/80 bg-green-500/10 border-green-500/20'
                    : data.calibration.correlation_score >= 0.7
                    ? 'text-yellow-400/80 bg-yellow-500/10 border-yellow-500/20'
                    : 'text-orange-400/80 bg-orange-500/10 border-orange-500/20'
                }`}
                data-testid="calibration-badge"
                title={`Smoothing: ${data.calibration.smoothing_window}, Stability: ${data.calibration.stability}`}
              >
                corr: {data.calibration.correlation_score?.toFixed(2)} | {data.calibration.stability}
              </span>
            )}
            {viewMode === 'activity' && data?.confidence && (
              <span
                className={`text-xs px-2 py-1 rounded border inline-flex items-center gap-1 ${
                  data.confidence.state === 'strong'
                    ? 'text-green-400/80 bg-green-500/10 border-green-500/20'
                    : data.confidence.state === 'good'
                    ? 'text-blue-400/80 bg-blue-500/10 border-blue-500/20'
                    : data.confidence.state === 'calibrating'
                    ? 'text-yellow-400/80 bg-yellow-500/10 border-yellow-500/20'
                    : 'text-red-400/80 bg-red-500/10 border-red-500/20'
                }`}
                data-testid="confidence-badge"
                title={`Confidence: ${(data.confidence.confidence * 100).toFixed(0)}% | Quality: ${data.data_quality?.level || '—'}`}
              >
                {data.confidence.state === 'strong' ? <Shield className="w-3 h-3" />
                  : data.confidence.state === 'unreliable' ? <AlertTriangle className="w-3 h-3" />
                  : <Activity className="w-3 h-3" />}
                {(data.confidence.confidence * 100).toFixed(0)}% {data.confidence.level}
              </span>
            )}
            {viewMode === 'activity' && data?.data_quality && (
              <span
                className={`text-xs px-2 py-1 rounded border ${
                  data.data_quality.level === 'high'
                    ? 'text-green-400/60 bg-green-500/5 border-green-500/15'
                    : data.data_quality.level === 'medium'
                    ? 'text-yellow-400/60 bg-yellow-500/5 border-yellow-500/15'
                    : data.data_quality.level === 'low'
                    ? 'text-orange-400/60 bg-orange-500/5 border-orange-500/15'
                    : 'text-red-400/60 bg-red-500/5 border-red-500/15'
                }`}
                data-testid="data-quality-badge"
                title={`Continuity: ${(data.data_quality.metrics?.continuity * 100).toFixed(0)}% | Coverage: ${(data.data_quality.metrics?.active_coverage * 100).toFixed(0)}%`}
              >
                quality: {data.data_quality.level}
              </span>
            )}
          </div>

          {/* Controls Row */}
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="bg-gray-800 border border-gray-600 rounded-md px-2.5 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
              data-testid="listener-report-date"
            />
            {!compareMode && (
              <Select value={selectedStation} onValueChange={setSelectedStation}>
                <SelectTrigger className="w-[220px] bg-gray-800 border-gray-600 text-sm text-gray-200" data-testid="listener-report-station-select">
                  <SelectValue placeholder={t('i18nB3_select_station')} />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600 max-h-[300px]">
                  <SelectItem value="global" className="text-gray-200">Όλοι οι σταθμοί (Global)</SelectItem>
                  {stations.map((s) => (
                    <SelectItem key={s.id} value={s.id} className="text-gray-200">{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button
              size="sm"
              variant={compareMode ? "default" : "outline"}
              onClick={toggleCompare}
              className={compareMode
                ? "bg-cyan-600 hover:bg-cyan-700 text-white border-cyan-500"
                : "border-gray-600 text-gray-300 hover:bg-gray-700"
              }
              data-testid="compare-toggle-btn"
            >
              {compareMode ? <X className="w-4 h-4 mr-1" /> : <GitCompareArrows className="w-4 h-4 mr-1" />}
              {compareMode ? t('i18nB3_close') : t('i18nB3_compare')}
            </Button>
            {!compareMode && (
              <Button size="sm" variant="outline" onClick={fetchReport} disabled={loading} className="border-gray-600 text-gray-300 hover:bg-gray-700" data-testid="listener-report-refresh">
                {loading ? <Clock className="w-4 h-4 animate-spin" /> : <ArrowUpDown className="w-4 h-4" />}
              </Button>
            )}
          </div>

          {/* ===== COMPARE MODE UI ===== */}
          {compareMode && (
            <div className="space-y-4">
              {/* Compare Type Tabs */}
              <div className="flex items-center gap-0.5 bg-gray-800/80 rounded-lg p-0.5 border border-gray-700/50 w-fit" data-testid="compare-type-tabs">
                {[
                  { value: 'station', label: t('i18nB3_station_vs_station') },
                  { value: 'group', label: t('i18nB3_group_vs_group') },
                  { value: 'category', label: t('i18nB3_genre_vs_genre') },
                ].map(tab => (
                  <button
                    key={tab.value}
                    onClick={() => { setCompareType(tab.value); setCompareA(''); setCompareB(''); setCompareData(null); }}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      compareType === tab.value
                        ? 'bg-cyan-600 text-white shadow-sm'
                        : 'text-gray-400 hover:text-gray-200'
                    }`}
                    data-testid={`compare-tab-${tab.value}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Compare Selectors */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    {compareType === 'station' ? t('i18nB3_station') : compareType === 'group' ? t('i18nB3_group') : t('i18nB3_genre')} A
                  </label>
                  <select
                    value={compareA}
                    onChange={(e) => setCompareA(e.target.value)}
                    className="w-full px-2.5 py-1.5 text-sm rounded-md border border-gray-600 bg-gray-800 text-cyan-300 focus:border-cyan-500 focus:outline-none"
                    data-testid="compare-select-a"
                  >
                    <option value="">-- Επιλέξτε --</option>
                    {getCompareOptions().map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">
                    {compareType === 'station' ? t('i18nB3_station') : compareType === 'group' ? t('i18nB3_group') : t('i18nB3_genre')} B
                  </label>
                  <select
                    value={compareB}
                    onChange={(e) => setCompareB(e.target.value)}
                    className="w-full px-2.5 py-1.5 text-sm rounded-md border border-gray-600 bg-gray-800 text-amber-300 focus:border-amber-500 focus:outline-none"
                    data-testid="compare-select-b"
                  >
                    <option value="">-- Επιλέξτε --</option>
                    {getCompareOptions().filter(o => o.value !== compareA).map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <Button
                    size="sm"
                    onClick={fetchCompareData}
                    disabled={!compareA || !compareB || compareLoading}
                    className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
                    data-testid="compare-fetch-btn"
                  >
                    {compareLoading ? <Clock className="w-4 h-4 animate-spin mr-1" /> : <GitCompareArrows className="w-4 h-4 mr-1" />}
                    Σύγκριση
                  </Button>
                </div>
              </div>

              {/* Compare Results */}
              {compareData && (
                <div className="space-y-4">
                  {/* Compare Labels */}
                  <div className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded-full bg-cyan-500" />
                      <span className="text-cyan-300 font-medium">{compareData.entity_a?.label}</span>
                    </span>
                    <span className="text-gray-600">vs</span>
                    <span className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded-full bg-amber-500" />
                      <span className="text-amber-300 font-medium">{compareData.entity_b?.label}</span>
                    </span>
                  </div>

                  {/* Compare Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="compare-summary">
                    <CompareSummaryCard
                      label="Peak"
                      valueA={formatNum(compareData.entity_a?.summary?.peak)}
                      valueB={formatNum(compareData.entity_b?.summary?.peak)}
                      icon={TrendingUp}
                      color="bg-rose-600/80"
                    />
                    <CompareSummaryCard
                      label="Avg"
                      valueA={formatNum(compareData.entity_a?.summary?.avg)}
                      valueB={formatNum(compareData.entity_b?.summary?.avg)}
                      icon={Users}
                      color="bg-blue-600/80"
                    />
                    <CompareSummaryCard
                      label="Min"
                      valueA={formatNum(compareData.entity_a?.summary?.min)}
                      valueB={formatNum(compareData.entity_b?.summary?.min)}
                      icon={TrendingDown}
                      color="bg-amber-600/80"
                    />
                    <CompareSummaryCard
                      label="TLH"
                      valueA={formatTLH(compareData.entity_a?.summary?.tlh)}
                      valueB={formatTLH(compareData.entity_b?.summary?.tlh)}
                      icon={Clock}
                      color="bg-emerald-600/80"
                    />
                  </div>

                  {/* Compare Chart */}
                  {compareChartData.some(d => d.hasData) && (
                    <div className="bg-gray-800/40 border border-gray-700/30 rounded-lg p-3 pt-4">
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={compareChartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 10 }} interval={1} />
                          <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                          <Tooltip content={renderTooltipContent} />
                          <Legend wrapperStyle={{ fontSize: 11, color: '#d1d5db' }} />
                          <Bar dataKey={`${compareData.entity_a?.label || 'A'} Avg`} fill="#06b6d4" radius={[3, 3, 0, 0]} opacity={0.85} />
                          <Bar dataKey={`${compareData.entity_b?.label || 'B'} Avg`} fill="#f59e0b" radius={[3, 3, 0, 0]} opacity={0.85} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Compare Table */}
                  <div className="overflow-x-auto rounded-lg border border-gray-700/40" data-testid="compare-table">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-800/80 text-gray-400 border-b border-gray-700/50">
                        <tr>
                          <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase">{t('i18nB3_hour')}</th>
                          <th className="px-2 py-2.5 text-center text-xs font-semibold text-cyan-400 border-l border-gray-700/30" colSpan={3}>
                            {compareData.entity_a?.label}
                          </th>
                          <th className="px-2 py-2.5 text-center text-xs font-semibold text-amber-400 border-l border-gray-700/30" colSpan={3}>
                            {compareData.entity_b?.label}
                          </th>
                        </tr>
                        <tr className="text-[10px] text-gray-500">
                          <th className="px-3 py-1"></th>
                          <th className="px-2 py-1 text-center border-l border-gray-700/30">Peak</th>
                          <th className="px-2 py-1 text-center">Avg</th>
                          <th className="px-2 py-1 text-center">TLH</th>
                          <th className="px-2 py-1 text-center border-l border-gray-700/30">Peak</th>
                          <th className="px-2 py-1 text-center">Avg</th>
                          <th className="px-2 py-1 text-center">TLH</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from({ length: 24 }, (_, h) => {
                          const a = (compareData.entity_a?.hourly || []).find(r => r.hour === h) || {};
                          const b = (compareData.entity_b?.hourly || []).find(r => r.hour === h) || {};
                          const hasData = (a.samples || 0) > 0 || (b.samples || 0) > 0;
                          return (
                            <tr
                              key={h}
                              className={`border-b border-gray-800/40 transition-colors ${
                                hasData
                                  ? h % 2 === 0 ? 'bg-gray-900/40' : 'bg-gray-800/20'
                                  : 'bg-gray-900/20 opacity-40'
                              } ${hasData ? 'hover:bg-gray-700/30' : ''}`}
                            >
                              <td className="px-3 py-1.5 font-mono text-gray-300 text-xs">{formatHour(h)}</td>
                              <td className="px-2 py-1.5 text-center text-cyan-300 text-xs border-l border-gray-700/30">{(a.samples || 0) > 0 ? formatNum(a.peak) : '—'}</td>
                              <td className="px-2 py-1.5 text-center text-cyan-200 text-xs">{(a.samples || 0) > 0 ? formatNum(a.avg) : '—'}</td>
                              <td className="px-2 py-1.5 text-center text-cyan-400/70 text-xs">{(a.samples || 0) > 0 ? formatTLH(a.tlh) : '—'}</td>
                              <td className="px-2 py-1.5 text-center text-amber-300 text-xs border-l border-gray-700/30">{(b.samples || 0) > 0 ? formatNum(b.peak) : '—'}</td>
                              <td className="px-2 py-1.5 text-center text-amber-200 text-xs">{(b.samples || 0) > 0 ? formatNum(b.avg) : '—'}</td>
                              <td className="px-2 py-1.5 text-center text-amber-400/70 text-xs">{(b.samples || 0) > 0 ? formatTLH(b.tlh) : '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ===== NORMAL MODE UI ===== */}
          {!compareMode && (
            <>
              {/* Info Badge */}
              {data?.station_id === 'global' && viewMode === 'audience' && (
                <div className="text-xs text-gray-500 bg-gray-800/40 rounded px-2.5 py-1.5 border border-gray-700/30" data-testid="global-info-badge">
                  Global = Avg ανά σταθμό (αποφυγή διπλομέτρησης). TLH = Total Listening Hours.
                </div>
              )}
              {data?.station_id === 'global' && viewMode === 'activity' && (
                <div className="text-xs text-gray-500 bg-gray-800/40 rounded px-2.5 py-1.5 border border-emerald-700/30" data-testid="global-activity-info-badge">
                  Global = Avg activity ανά σταθμό. Engagement Load = Σύνθετο behavioral score (polls, title changes, listener dynamics).
                </div>
              )}

              {/* Daily Summary Cards */}
              {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="listener-report-summary">
                  <SummaryCard label={labels.peak} value={formatNum(summary.peak)} icon={TrendingUp} color="bg-rose-600/80" />
                  <SummaryCard label={labels.avg} value={formatNum(summary.avg)} icon={Users} color="bg-blue-600/80" />
                  <SummaryCard label={labels.min} value={formatNum(summary.min)} icon={TrendingDown} color="bg-amber-600/80" />
                  <SummaryCard label={labels.tlh} value={formatTLH(summary.tlh)} icon={Clock} color="bg-emerald-600/80" />
                </div>
              )}

              {/* Live Sampling Pulse */}
              {viewMode === 'audience' && (
                <ListenerSamplingPulse token={token} />
              )}

              {/* Charts */}
              {hasChartData && (
                <Tabs defaultValue="bar" className="w-full" data-testid="listener-chart-tabs">
                  <TabsList className="bg-gray-800/80 border border-gray-700/50">
                    <TabsTrigger value="bar" className="data-[state=active]:bg-gray-700 text-gray-300 text-xs" data-testid="chart-tab-bar">Bar Chart</TabsTrigger>
                    <TabsTrigger value="line" className="data-[state=active]:bg-gray-700 text-gray-300 text-xs" data-testid="chart-tab-line">Line Chart</TabsTrigger>
                  </TabsList>
                  <TabsContent value="bar" data-testid="chart-bar-content">
                    <div className="bg-gray-800/40 border border-gray-700/30 rounded-lg p-3 pt-4">
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 11 }} interval={1} />
                          <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                          <Tooltip content={renderTooltipContent} />
                          <Legend wrapperStyle={{ fontSize: 12, color: '#d1d5db' }} formatter={legendFormatter} />
                          <Bar dataKey="Peak" fill="#f43f5e" radius={[3, 3, 0, 0]} opacity={0.85} />
                          <Bar dataKey="Avg" fill="#3b82f6" radius={[3, 3, 0, 0]} opacity={0.85} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </TabsContent>
                  <TabsContent value="line" data-testid="chart-line-content">
                    <div className="bg-gray-800/40 border border-gray-700/30 rounded-lg p-3 pt-4">
                      <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 11 }} interval={1} />
                          <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                          <Tooltip content={renderTooltipContent} />
                          <Legend wrapperStyle={{ fontSize: 12, color: '#d1d5db' }} formatter={legendFormatter} />
                          <Line type="monotone" dataKey="Peak" stroke="#f43f5e" strokeWidth={2} dot={{ r: 3, fill: '#f43f5e' }} activeDot={{ r: 5 }} />
                          <Line type="monotone" dataKey="Avg" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3, fill: '#3b82f6' }} activeDot={{ r: 5 }} />
                          <Line type="monotone" dataKey="Min" stroke="#f59e0b" strokeWidth={1.5} dot={{ r: 2, fill: '#f59e0b' }} strokeDasharray="4 2" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </TabsContent>
                </Tabs>
              )}

              {/* Hourly Table */}
              {loading ? (
                <div className="space-y-2">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="h-8 bg-gray-800/60 rounded animate-pulse" />
                  ))}
                </div>
              ) : sortedHourly.length > 0 ? (
                <div className="overflow-x-auto rounded-lg border border-gray-700/40" data-testid="listener-report-table">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-800/80 text-gray-400 border-b border-gray-700/50">
                      <tr>
                        <SortHeader label={t('i18nB3_hour')} field="hour" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                        <SortHeader label={labels.peak} field="peak" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                        <SortHeader label={labels.avg} field="avg" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                        <SortHeader label={labels.min} field="min" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                        <SortHeader label={labels.tlh} field="tlh" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                        <SortHeader label="Samples" field="samples" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                        {data?.station_id === 'global' && <SortHeader label={t('i18nB3_stations')} field="station_count" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedHourly.map((row, idx) => {
                        const hasData = row.samples > 0;
                        return (
                          <tr
                            key={row.hour}
                            className={`border-b border-gray-800/40 transition-colors ${
                              hasData
                                ? idx % 2 === 0 ? 'bg-gray-900/40' : 'bg-gray-800/20'
                                : 'bg-gray-900/20 opacity-40'
                            } ${hasData ? 'hover:bg-gray-700/30' : ''}`}
                            data-testid={`hour-row-${row.hour}`}
                          >
                            <td className="px-3 py-2 font-mono text-gray-300">{formatHour(row.hour)}</td>
                            <td className="px-3 py-2 text-rose-300 font-medium">{hasData ? formatNum(row.peak) : '—'}</td>
                            <td className="px-3 py-2 text-blue-300 font-medium">{hasData ? formatNum(row.avg) : '—'}</td>
                            <td className="px-3 py-2 text-amber-300">{hasData ? formatNum(row.min) : '—'}</td>
                            <td className="px-3 py-2 text-emerald-300">{hasData ? formatTLH(row.tlh) : '—'}</td>
                            <td className="px-3 py-2 text-gray-400">{hasData ? row.samples : '—'}</td>
                            {data?.station_id === 'global' && (
                              <td className="px-3 py-2 text-gray-400">{hasData ? row.station_count : '—'}</td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500" data-testid="listener-report-empty">
                  <Users className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p>Δεν υπάρχουν δεδομένα για {selectedDate}</p>
                </div>
              )}

              {/* Footer info */}
              <div className="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                <span>Ημ/νία: {data?.date || selectedDate}</span>
                <span>{t('i18nB3_station')}: {data?.station_name || '—'}</span>
                {summary?.samples && <span>Δείγματα: {summary.samples}</span>}
                {summary?.hours_with_data != null && <span>Ωρες με δεδομένα: {summary.hours_with_data}</span>}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ListenerReport;
